<template>
  <header class="flex align-items-center shadow-2 noprint">
    <div class="container flex align-items-center">
      <!-- branding/link to home page -->

      <div class="flex flex-row mr-auto">
        <router-link :to="{ name: 'Home' }">
          <img
            class="brand"
            :src="isDarkMode ? '/img/logo-dark.png' : '/img/logo-light.png'"
          />
        </router-link>
      </div>
      <!-- Desktop nav -->
      <nav>
        <ul class="flex">
          <li v-for="page in pages" :key="page.label">
            <router-link
              v-if="page.name"
              class="nav-item"
              :to="{ name: page.name }"
              >{{ page.label }}</router-link
            >
            <a
              v-else-if="page.url"
              class="nav-item"
              target="_blank"
              :href="page.url"
              rel="noopener noreferrer"
              >{{ page.label }}</a
            >
          </li>
        </ul>
      </nav>

      <!-- Admin Dropdown "button" that looks like a plain link -->
      <Button
        v-if="user.is_admin"
        ref="toggleadminbtn"
        aria-haspopup="true"
        aria-controls="admin-nav-menu"
        class="p-button p-button-text p-button-plain admin-menu-button hidden md:block"
        @click="toggleAdminNavDropdown"
      >
        Admin
      </Button>

      <!-- Dropdown admin nav menu -->
      <Menu
        id="admin-nav-menu"
        ref="adminsubmenu"
        :model="adminPages"
        :popup="true"
      />

      <!-- Online indicator -->
      <span
        v-tooltip.left="tooltipText"
        class="online-indicator mr-2 p-2"
        :class="isOnline ? 'online' : 'offline'"
      >
        <icon
          v-if="inFlight"
          :icon="['far', 'spinner-third']"
          spin
          color="$warning-400"
          style="--fa-animation-duration: 0.7s"
        />
        <i v-else class="pi pi-wifi" />
      </span>

      <!-- Hamburger nav button on mobile -->
      <Button
        ref="togglenavbtn"
        type="button"
        icon="pi pi-bars"
        class="p-button-sm hamburger"
        aria-haspopup="true"
        aria-controls="mobile-nav-menu"
        @click="toggleMobileNavDropdown"
      />

      <!-- Dropdown mobile nav menu -->
      <Menu
        id="mobile-nav-menu"
        ref="mobilenavmenu"
        :model="mobilePages"
        :popup="true"
      />

      <Button
        v-if="authState && authState.isAuthenticated"
        class="p-button-sm mx-2"
        @click="logout"
      >
        Log Out
      </Button>
      <Button v-else class="p-button-sm mx-2" @click="login">Log In</Button>

      <DarkModeSwitch />
    </div>
  </header>
</template>

<script setup lang="ts">
import { nextTick, ref, computed } from "vue";
import { useOnline } from "@vueuse/core";
import Menu from "primevue/menu";
import useUserInfo from "@/composables/auth/useUserInfo";
import DarkModeSwitch from "./DarkModeSwitch.vue";
import { inFlight } from "@/composables/exchanges/mutationLimiter";
import { useAuth } from "@okta/okta-vue";
import { discardConfirmationKey } from "@/plugins/confirmationPlugin";

import { injectStrict } from "@/utils/utils";
import { useSettingsStore } from "@/store";
import { useRouter } from "vue-router";

const { isDarkMode } = useSettingsStore();
const isOnline = useOnline();

const sk_url = window.config.SUMMIT_KNOWLEDGE_URL;
const router = useRouter();
const authClient = useAuth();
const user = useUserInfo();
const pages = computed(() =>
  [
    { label: "My Sizings", name: "Home", visible: true },
    { label: "All Sizings", name: "Sizings", visible: true },
    { label: "Quotes", name: "ListQuotes", visible: !user.is_customer },
    { label: "SK", url: sk_url, visible: true },
    { label: "User", name: "User", visible: true }
    // {
    //   label: "Legacy Importer",
    //   to: "/legacy-importer",
    //   visible: !!(user.is_admin && isOnline.value)
    // }
  ].filter(item => item.visible)
);

const adminPages = computed(() => [
  {
    label: "Equipment",
    command: () =>
      router.push({
        name: "Casings"
      }),
    visible: true
  },
  {
    label: "Deleted Sizings",
    command: () =>
      router.push({
        name: "DeletedSizings"
      }),
    visible: true
  },
  {
    label: "Data Dashboard",
    command: () =>
      router.push({
        name: "DataDashboard"
      }),
    visible: true
  },
  {
    label: "EULA",
    command: () =>
      router.push({
        name: "Eula"
      }),
    visible: true
  }
]);

const mobilenavmenu = ref();
const togglenavbtn = ref();
const adminsubmenu = ref();
const toggleadminbtn = ref();

// Dumb hack alert
// Z indexes don't start incrementing until something is triggered
// without this the network tooltip doesn't show up
nextTick(() => {
  togglenavbtn.value.$el.click();
  nextTick(() => {
    mobilenavmenu.value?.hide();
  });
});

const tooltipText = computed(() =>
  inFlight.value
    ? `Saving Changes`
    : `Connection is ${isOnline.value ? "online" : "offline"}`
);

const toggleMobileNavDropdown = (event: MouseEvent) =>
  mobilenavmenu.value.toggle(event);

const toggleAdminNavDropdown = (event: MouseEvent) =>
  adminsubmenu.value.toggle(event);

const { toggleDiscardChanges, isDirty } = injectStrict(discardConfirmationKey);

const mapPageToCommands = (pages: Array<{ name?: string }>) => {
  return pages.map(page => {
    const { name, ...restOfPage } = page;
    if (!name) {
      return restOfPage;
    }
    return {
      ...restOfPage,
      command: () => {
        router.push({
          name
        });
      }
    };
  });
};

const mobilePages = computed(() => {
  const formattedPages = mapPageToCommands(pages.value);
  if (user.is_admin) {
    return [
      ...formattedPages,
      {
        label: "Admin",
        items: adminPages.value
      }
    ];
  }
  return formattedPages;
});

async function logout() {
  if (isDirty()) {
    const continued = await toggleDiscardChanges();
    if (!continued) return;
  }
  await authClient.signOut({
    postLogoutRedirectUri: `${window.location.origin}/logout`
  });
}

function login() {
  authClient.signInWithRedirect();
}
</script>

<style lang="scss" scoped>
header {
  background-color: var(--surface-app-header);
  z-index: 1;
  height: 65px;
  transition: all 0.2s ease-in-out;
}
.hamburger {
  display: none;
}

.online-indicator {
  &.online {
    background-color: var(--success-500);
  }
  &.offline {
    background-color: var(--warning-500);
  }
  color: white;
  padding: 5px;
  border-radius: 5px;
}

ul {
  list-style-type: none;
}

.brand {
  max-height: 36px;
}

.nav-item {
  padding: 0.5em 1em;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: -1.25em;
  transition: all 0.3s ease-in-out;
  color: var(--text-color);

  &:hover {
    color: var(--danger-500);
    text-decoration-color: transparent;
  }
  &.router-link-exact-active {
    color: var(--danger-600);
    text-decoration-color: var(--danger-600);
    font-weight: 500;
  }
}

.p-button.p-button-text.p-button-plain.admin-menu-button {
  margin-right: 1em;
  background: none;
}

.p-button.p-button-text.p-button-plain.admin-menu-button:hover {
  background: none;
}

@media screen and (max-width: 1200px) {
  header {
    nav,
    .admin-menu-button {
      display: none !important;
    }
    .hamburger {
      display: flex;
    }
  }
}

@media only screen and (max-width: 750px) {
  .brand {
    max-height: 20px;
  }
}
</style>
